import { useState } from 'react';
import { Edit2, Unlink } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { getGroupedClientFields } from '../../data/clientFieldsConfig';
import { useAuthStore } from '../../stores/authStore';
import { useClientConnections, useClientActivity } from './hooks';
import { formatMoney } from '../../lib/money';
import MoneyDefaultsSection from '../../components/money/clients/MoneyDefaultsSection';
import type { ClientRow } from './hooks';

// Label map for client_connections.connected_via values. Keep open — unknown
// values fall through to the raw string rather than "unknown" so new enum
// values surface visibly in the UI instead of getting swallowed.
const CONNECTED_VIA_LABEL: Record<string, string> = {
  code: 'code',
  manual: 'added manually',
  chat_invite: 'chat invite',
};

interface Props {
  client: ClientRow | null;
  businessId: string;
  userId: string;
  onClose: () => void;
  onEdit: (c: ClientRow) => void;
  onDisconnected: () => void;
}

export function ClientDetailPanel({ client, businessId, userId, onClose, onEdit, onDisconnected }: Props) {
  const { connections, disconnect } = useClientConnections(client?.user_id ?? null, businessId);
  const { bookings, invoices, projects } = useClientActivity(client?.id ?? null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const categoryId = useAuthStore((s) => s.business?.industry ?? null);
  const groupedFields = getGroupedClientFields(categoryId);

  if (!client) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cf = (client as any).custom_fields as Record<string, any> | undefined;
  const hasCustomFieldValues = cf && groupedFields.some(g => g.fields.some(f => {
    const v = cf[f.key];
    return v != null && v !== '' && v !== false;
  }));

  const handleDisconnect = async () => {
    if (!confirmDisconnect) return;
    setDisconnecting(true);
    try {
      const ok = await disconnect(confirmDisconnect, userId);
      if (ok) { setConfirmDisconnect(null); onDisconnected(); }
    } finally {
      setDisconnecting(false);
    }
  };

  const firstName = client.name.split(' ')[0] || client.name;

  const activeConns = connections.filter(c => c.status !== 'disconnected');

  return (
    <SlidePanel isOpen={!!client} onClose={onClose} title={client.name} subtitle={client.company_name ?? undefined} width={560}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ fontSize: 13 }}>
          {client.email && <div style={{ color: palette.g600, marginBottom: 2 }}>{client.email}</div>}
          {client.phone && <div style={{ color: palette.g600, marginBottom: 2 }}>{client.phone}</div>}
          {client.address && <div style={{ color: palette.g500, marginBottom: 2 }}>{client.address}</div>}
        </div>
        <button onClick={() => onEdit(client)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 12, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Edit2 size={12} /> Edit
        </button>
      </div>

      {/* Tags */}
      {client.tags && client.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
          {client.tags.map(tag => (
            <span key={tag} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: palette.accentWash, color: palette.accentDeep }}>{tag}</span>
          ))}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, padding: '10px 12px', background: palette.g50, borderRadius: 6, fontSize: 12 }}>
        <div><span style={{ color: palette.g400 }}>Bookings</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>{client.booking_count}</div></div>
        <div><span style={{ color: palette.g400 }}>Invoices</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>{client.invoice_count}</div></div>
        <div><span style={{ color: palette.g400 }}>Projects</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>{client.project_count}</div></div>
        <div><span style={{ color: palette.g400 }}>Revenue</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>${client.invoice_total.toLocaleString('en-CA')}</div></div>
      </div>

      {/* Industry-specific details */}
      {hasCustomFieldValues && cf && (
        <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Industry Details</div>
          {groupedFields.map(group => {
            const fieldsWithValues = group.fields.filter(f => {
              const v = cf[f.key];
              return v != null && v !== '' && v !== false;
            });
            if (fieldsWithValues.length === 0) return null;
            return (
              <div key={group.section} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{group.section}</div>
                {fieldsWithValues.map(f => {
                  let display = String(cf[f.key]);
                  if (f.type === 'boolean') display = cf[f.key] ? 'Yes' : 'No';
                  if (f.type === 'date' && cf[f.key]) display = new Date(cf[f.key]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  return (
                    <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}>
                      <span style={{ color: palette.g500 }}>{f.label}</span>
                      <span style={{ color: palette.g800, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{display}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Connections */}
      {activeConns.length > 0 && (
        <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Connections</div>
          {activeConns.map(conn => (
            <div key={conn.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12 }}>
              <span style={{ padding: '2px 8px', borderRadius: 4, background: palette.greenWash, color: palette.green, fontWeight: 600, fontSize: 10 }}>Connected</span>
              <span style={{ color: palette.g500 }}>via {conn.connected_via ? (CONNECTED_VIA_LABEL[conn.connected_via] ?? conn.connected_via) : 'unknown'}</span>
              {conn.code_used && <span style={{ color: palette.g400 }}>code: {conn.code_used}</span>}
              <span style={{ color: palette.g400, flex: 1, textAlign: 'right' }}>{conn.connected_at?.split('T')[0]}</span>
              <button onClick={() => setConfirmDisconnect(conn.id)} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 3, border: 'none', background: palette.redWash, color: palette.red, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <Unlink size={10} /> Disconnect
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Recent Bookings */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Recent Bookings</div>
        {bookings.length === 0 ? <div style={{ fontSize: 12, color: palette.g400 }}>None</div> : (
          bookings.map(b => (
            <div key={b.id} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12, cursor: 'pointer' }}>
              <span style={{ color: palette.g500 }}>{b.date}</span>
              <span style={{ padding: '0 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: b.status === 'completed' ? palette.greenWash : b.status === 'cancelled' ? palette.redWash : palette.blueWash, color: b.status === 'completed' ? palette.green : b.status === 'cancelled' ? palette.red : palette.blue }}>{b.status}</span>
            </div>
          ))
        )}
      </div>

      {/* Recent Invoices */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Recent Invoices</div>
        {invoices.length === 0 ? <div style={{ fontSize: 12, color: palette.g400 }}>None</div> : (
          invoices.map(i => (
            <div key={i.id} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12, cursor: 'pointer' }}>
              <span style={{ color: palette.g500 }}>{i.created_at.split('T')[0]}</span>
              <span style={{ padding: '0 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: i.status === 'paid' ? palette.greenWash : palette.amberWash, color: i.status === 'paid' ? palette.green : palette.amber }}>{i.status}</span>
              <span style={{ flex: 1, textAlign: 'right', fontWeight: 600, color: palette.g800 }}>{formatMoney(i.total)}</span>
            </div>
          ))
        )}
      </div>

      {/* Recent Projects */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Recent Projects</div>
        {projects.length === 0 ? <div style={{ fontSize: 12, color: palette.g400 }}>None</div> : (
          projects.map(p => (
            <div key={p.id} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12, cursor: 'pointer' }}>
              <span style={{ flex: 1, color: palette.g700, fontWeight: 500 }}>{p.name}</span>
              <span style={{ padding: '0 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: palette.accentWash, color: palette.accentDeep }}>{p.status}</span>
            </div>
          ))
        )}
      </div>

      {/* Notes */}
      {client.notes && (
        <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 6 }}>Notes</div>
          <div style={{ fontSize: 13, color: palette.g600, padding: '8px 10px', background: palette.g50, borderRadius: 6 }}>{client.notes}</div>
        </div>
      )}

      {/* Money defaults — pre-fill on every invoice for this client. */}
      <MoneyDefaultsSection clientId={client.id} clientName={client.name} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
      </div>

      <ConfirmDialog
        isOpen={!!confirmDisconnect}
        title={`Disconnect ${firstName}?`}
        message={`${firstName} will lose access to your business through their client app. Past bookings, invoices, and messages stay in your records. ${firstName} can reconnect later with a new code.`}
        confirmLabel="Disconnect"
        confirmVariant="danger"
        loading={disconnecting}
        onClose={() => { if (!disconnecting) setConfirmDisconnect(null); }}
        onConfirm={handleDisconnect}
      />
    </SlidePanel>
  );
}
